import { Intersection, Points, Raycaster, Vector3 } from 'three';

import { SkyviewCamera } from '../cameras/types';
import { getAttributePositionAtIndex } from '../geodata/point-clouds/cesium-point-cloud/points/buffer-attribute/position-buffer-attribute';

/**
 * How far from the cursor, in CSS pixels, a point of the timeline's point cloud or tiled model is
 * picked. A fixed pixel radius tightens the radius in the world as the camera gets closer, so close
 * up picks stay precise while distant ones stay easy to grab.
 */
export const timelinePickPixels = 20;

/**
 * Moves the hits on points to the points' own positions, from the position on the ray closest to
 * them, with their distance to the ray. Needs the points' world matrices to be up to date.
 */
export function toPointPositions(rayCaster: Raycaster, intersections: Intersection[]) {
  for (const intersection of intersections) {
    if (intersection.object instanceof Points && intersection.index !== undefined) {
      intersection.point = intersection.object.localToWorld(
        getAttributePositionAtIndex(intersection.object, intersection.index),
      );
      intersection.distanceToRay = rayCaster.ray.distanceToPoint(intersection.point);
    }
  }
  return intersections;
}

/**
 * Picks the front-most of the hits of a ray from the camera through the cursor that land within
 * the pixel radius of the cursor on screen, so the surface closest to the camera wins over points
 * behind it that are closer to the ray. Hits on a mesh are on the ray, so they always land there.
 * Hits outside the camera's depth range are skipped: the renderer clips them, so they aren't seen.
 *
 * @param intersections Gathered generously around the ray, at the positions they are at, see
 * toPointPositions.
 */
export function pickNearCursor(
  camera: SkyviewCamera,
  rayCaster: Raycaster,
  intersections: Intersection[],
  pixels = timelinePickPixels,
): Intersection | undefined {
  // Every point on the ray lands on the cursor
  const cursor = camera.getScreenPosition(rayCaster.ray.at(1, new Vector3()));

  let frontMost: Intersection | undefined;
  for (const intersection of intersections) {
    const depth = intersection.point.clone().project(camera.getTHREECamera()).z;
    if (
      depth >= -1 &&
      depth <= 1 &&
      camera.getScreenPosition(intersection.point).distanceTo(cursor) <= pixels &&
      (!frontMost || intersection.distance < frontMost.distance)
    ) {
      frontMost = intersection;
    }
  }

  return frontMost;
}

/**
 * Picks the hit closest to the ray, for rays that aren't from the camera, e.g. along a line.
 */
export function closestToRay(intersections: Intersection[]): Intersection | undefined {
  return intersections.reduce<Intersection | undefined>(
    (closest, intersection) =>
      !closest || intersection.distanceToRay! < closest.distanceToRay! ? intersection : closest,
    undefined,
  );
}
